import React, { PropsWithChildren, ReactElement } from 'react';
import { connect } from 'react-redux';
import { formInitialValues } from '../formModel/form.initial.values';
import { Form, Formik, FormikProps } from 'formik';
import validationSchemas from '../formModel/validation.schema';
import {
    Button,
    Card,
    CardActionArea,
    CardActions,
    FormControl,
    FormGroup,
    FormHelperText,
    Grid,
    TextField,
    InputAdornment,
} from '@material-ui/core';
import styles from '../index.module.scss';
import formModels from '../formModel/form.model';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import en from '../../../../../../assets/language/en.json';
import { globalConstants } from '../../../../../shared/constants/global.constants';
import { filterAction } from '../../../filter.actions';
import { genericHelpers } from '../../../../../shared/helpers/generics';
import ImportFile from '../../../../import/pages/import.file';
import { IFilterForm, IFilterFormComponent } from '../../../filter.interfaces';
import clsx from 'clsx';
import LanguageFlag from '../../../../../shared/components/generics/languageFlag/LanguageFlag';
import AltText from '../../../../../shared/components/generics/AltText/AltText';

type Props = IFilterFormComponent & typeof formModels.filterFormModel;

const FilterForm = (props: PropsWithChildren<Props>): ReactElement => {
    const {
        formField: { name_en, name_de, picture, pictureAltTextDe, pictureAltTextEn },
        filter,
    } = props;

    const formInitials = filter ?? formInitialValues.filter;
    const hiddenPictureInput = React.useRef(null);

    const handleSubmit = (values: IFilterForm): void => {
        filter ? handleUpdate(values) : handleInsert(values);
    };

    const handleInsert = (values: IFilterForm): void => {
        props.addFilter(values).then((response) => {
            props.handleCompleteStep(response);
        });
    };
    const handleUpdate = (values: IFilterForm): void => {
        props.updateFilter(genericHelpers.removeIdFromObject(values), values.id).then((response) => {
            props.handleCompleteStep(response);
        });
    };

    const handleUploadClick = (): void => {
        hiddenPictureInput.current.click();
    };

    const nextStep = (): void => {
        props.nextStep(filter);
    };

    return (
        <div className={clsx(styles.filter, 'mt8')}>
            <Formik
                initialValues={formInitials}
                onSubmit={(values: IFilterForm, { setSubmitting }) => {
                    handleSubmit(values);
                    setSubmitting(false);
                }}
                validationSchema={validationSchemas.filterValidationSchema}
                enableReinitialize={true}
            >
                {(props: FormikProps<IFilterForm>) => (
                    <Form className={styles.addFilterForm}>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <FormGroup className={styles.inputGroup}>
                                    <FormControl className={clsx(styles.formControl, 'mb4')} required>
                                        <TextField
                                            className={styles.formInput}
                                            label={name_en.label}
                                            name={name_en.name}
                                            type="text"
                                            onChange={props.handleChange}
                                            onBlur={props.handleBlur}
                                            value={props.values.name_en}
                                            variant="outlined"
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <LanguageFlag country="us" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <FormHelperText className={clsx(styles.error_span, 'mt2')}>
                                            {props.errors.name_en && props.touched.name_en ? props.errors.name_en : ''}
                                        </FormHelperText>
                                    </FormControl>

                                    <FormControl className={clsx(styles.formControl, 'mb4')} required>
                                        <TextField
                                            className={styles.formInput}
                                            label={name_de.label}
                                            name={name_de.name}
                                            type="text"
                                            onChange={props.handleChange}
                                            onBlur={props.handleBlur}
                                            value={props.values.name_de}
                                            variant="outlined"
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <LanguageFlag country="de" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <FormHelperText className={clsx(styles.error_span, 'mt2')}>
                                            {props.errors.name_de && props.touched.name_de ? props.errors.name_de : ''}
                                        </FormHelperText>
                                    </FormControl>
                                </FormGroup>
                            </Grid>
                            <Grid item xs={6}>
                                <FormGroup className={styles.inputGroup}>
                                    <FormControl className={clsx(styles.formControl, 'mb4')}>
                                        <Card>
                                            <CardActionArea>
                                                <ImportFile
                                                    file={props.values.picture}
                                                    title={en.header_picture}
                                                    style={styles.imgCard}
                                                    remove={() => props.setFieldValue('picture', null)}
                                                />
                                            </CardActionArea>
                                            <CardActions>
                                                <input
                                                    accept="file/"
                                                    id="importFile"
                                                    type="file"
                                                    className={styles.fileInput}
                                                    ref={hiddenPictureInput}
                                                    onChange={(event) => {
                                                        props.setFieldValue(
                                                            picture.name,
                                                            event.currentTarget.files[
                                                                globalConstants.KEYS.FIRST_FILE_INDEX
                                                            ],
                                                        );
                                                    }}
                                                />
                                                <Button
                                                    className={styles.uploadImg}
                                                    onClick={handleUploadClick}
                                                    startIcon={<CloudUploadIcon />}
                                                >
                                                    {en.upload_full_img}
                                                </Button>
                                            </CardActions>
                                        </Card>
                                    </FormControl>
                                </FormGroup>
                                <AltText
                                    {...props}
                                    altTextEnFieldMeta={pictureAltTextEn}
                                    altTextDeFieldMeta={pictureAltTextDe}
                                />
                            </Grid>
                        </Grid>
                        <div className={styles.bottomNavigation}>
                            <Button
                                className={clsx(styles.bottomNavigationButton, 'mr2', 'mt4')}
                                onClick={nextStep}
                                disabled={!filter}
                            >
                                {en.next}
                            </Button>
                            <Button
                                type="submit"
                                className={clsx(styles.bottomNavigationButton, 'mr2', 'mt4')}
                                disabled={props.isSubmitting || !(props.isValid && props.dirty)}
                            >
                                {en.save_button_label}
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

const mapDispatchToProps = {
    addFilter: filterAction.addFilter,
    updateFilter: filterAction.updateFilter,
    getFilterById: filterAction.getFilterById,
};

export default connect(null, mapDispatchToProps)(FilterForm);
